/* eslint-disable react/no-this-in-sfc */
import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Box } from '@material-ui/core';
import _ from 'lodash';

import PerformanceChart from '../performance-chart/PerformanceChart';
import { statusBreakoutDef, statusDef } from '../performance-chart/defs';

import { useStyles } from './StatusBreakoutStyles';
import {
    statusOfSlot,
    initSelectionIndicator,
    applyStatusFilter,
    initSelectionLabel,
    timeGranularityFormatter,
    isXCoordinateWithinSelectionLabel,
    getLabelPositionAgainstChart,
    statusCountsToMetrics,
} from './funcs';
import { getStatusColor } from '../../../utilities/StatusUtilities';
import { useStatusFilter } from '../../filters';
import { usePageStateValue } from '../../../stores/PageStateStore';
import useSize from '../../../utilities/UseSize';
import { getPointWidth, getTimes } from '../data-series';

const CURRENT = 'current';
const CUSTOM = 'custom';
const SELECTED_TIME = 'SelectedTime';

const inTimeRange = (times, date) => date && date >= times.start && date <= times.end;

export default function StatusBreakout({
    disableTimeAxis,
    noSelection,
    disableSelection,
    height,
    metricName,
    data,
    onSetPositions,
    onTimeRangeChange,
    isStatus,
    clearTimeRange,
}) {
    const classes = useStyles();
    const containerRef = useRef();
    const { width } = useSize(containerRef);
    const [pointWidth, setPointwidth] = useState(0);
    const [countsData, setCountsData] = useState();
    const [positions, setPositions] = useState({});
    const [widths, setWidths] = useState({});
    const times = useMemo(() => getTimes([metricName], data), [metricName, data]);
    const [holder, setHolder] = useState();
    const [selection, setSelection] = useState({ mode: CURRENT });
    const [selectedTime, selectedTimeActions] = usePageStateValue({
        key: SELECTED_TIME,
        defaultValue: 0,
    });
    const statusFilter = useStatusFilter();
    const renderSelection = useRef();
    const destroySelection = useRef();

    renderSelection.current = () => {
        const { chart } = widths;

        if (disableTimeAxis || disableSelection || !times || !chart || !pointWidth || !selection.date) return;

        const pos = getLabelPositionAgainstChart(times, width, pointWidth, pointWidth, selection.date);
        if (holder?.selectionIndicator) {
            holder.selectionIndicator.destroy();
            holder.selectionIndicator = undefined;
        }
        const myIndicator = initSelectionIndicator(chart, pointWidth);
        const status = statusOfSlot(metricName, statusFilter, data, selection.date);
        const statusColor = getStatusColor(status);
        myIndicator.pointerElement.attr({
            fill: statusColor,
        });
        myIndicator.translate(pos, chart.plotTop);

        let selectionLabel;
        if (!holder?.selectionLabel) {
            selectionLabel = initSelectionLabel(chart, selection.date, selection.granularity);
        } else {
            selectionLabel = holder.selectionLabel;
        }
        selectionLabel.attr({
            text: timeGranularityFormatter(selection.date, selection.granularity),
        });
        const labelBBox = selectionLabel.element.getBBox();
        const xAxis = _.find(chart.axes, (axis) => axis.isXAxis);
        const labelX = getLabelPositionAgainstChart(times, width, labelBBox.width, pointWidth, selection.date);
        selectionLabel.translate(labelX, chart.plotTop + height);
        xAxis.update({});

        myIndicator.removeClass('hidden');
        myIndicator.toFront();

        if (!holder) {
            setHolder({ selectionIndicator: myIndicator, selectionLabel });
        } else {
            holder.selectionIndicator = myIndicator;
        }
    };

    destroySelection.current = () => {
        if (holder?.selectionIndicator) holder.selectionIndicator.destroy();
        if (holder?.selectionLabel) holder.selectionLabel.destroy();
        setHolder(undefined);
    };

    useEffect(() => {
        return () => {
            destroySelection.current();
        };
    }, []);

    useEffect(() => {
        if (disableSelection || disableTimeAxis) destroySelection.current();
    }, [disableSelection, disableTimeAxis]);

    useEffect(() => {
        renderSelection.current();
    }, [data, selection, height, width, widths, pointWidth]);

    useLayoutEffect(() => {
        if (!widths.chart || !times) return;

        const newPos = getLabelPositionAgainstChart(times, width, pointWidth, pointWidth, selection.date);

        const newPositions = {
            chart: widths.chart,
            chartWidth: width,
            pointWidth,
            position: newPos,
        };

        if (!_.isEqual(newPositions, positions)) {
            setPositions(newPositions);
            onSetPositions(newPositions);
        }
    }, [widths, times, selection, positions, pointWidth, width, onSetPositions]);

    useEffect(() => {
        if (clearTimeRange) {
            setSelection({ mode: CURRENT, date: 60_000, granularity: 60_000 });
            onTimeRangeChange({ start: 60_000, end: 120_000 });
        }
    }, [clearTimeRange, onTimeRangeChange]);

    useEffect(() => {
        if (!times) return;

        if (selection.mode === CUSTOM) {
            if (inTimeRange(times, selection.date)) return;
        }

        if (selection.date !== times.last && times.last) {
            setSelection({
                mode: CURRENT,
                date: times.last,
                granularity: times.granularity,
            });
            onTimeRangeChange({ start: times.last, end: times.last + times.granularity });
        }
    }, [data, metricName, selection, onTimeRangeChange, times]);

    useEffect(() => {
        if (!times) return;

        if (
            inTimeRange(times, selectedTime) &&
            selectedTime &&
            (selectedTime !== selection.date || selection.mode === CURRENT)
        ) {
            setSelection({ mode: CUSTOM, date: selectedTime, granularity: times.granularity });
            onTimeRangeChange({ start: selectedTime, end: selectedTime + times.granularity });
        }
        // this useEffect sets and uses selection
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTime, onTimeRangeChange, times, pointWidth]);

    const xAxis = useMemo(() => {
        if (disableTimeAxis || !times) return undefined;

        function xAxisLabelFormatter() {
            const tick = this;
            const selectionLabel = holder?.selectionLabel;
            const time = selection?.date;
            const x = tick.axis.toPixels(tick.value);

            if (
                !disableSelection &&
                isXCoordinateWithinSelectionLabel(x, selectionLabel, tick.chart, time, times, width, pointWidth)
            ) {
                return '';
            }

            return tick.axis.defaultLabelFormatter.call(tick);
        }

        function xAxisTickPositioner() {
            const selectionLabel = holder?.selectionLabel;
            const time = selection?.date;

            if (disableSelection || disableTimeAxis) return;

            _.remove(this.tickPositions, (value) => {
                const tickXValue = this.toPixels(value);
                return isXCoordinateWithinSelectionLabel(
                    tickXValue,
                    selectionLabel,
                    this.chart,
                    time,
                    times,
                    width,
                    pointWidth
                );
            });
        }

        return {
            labels: {
                formatter: xAxisLabelFormatter,
            },
            tickPositioner: xAxisTickPositioner,
            gridLineWidth: 0,
        };
    }, [disableTimeAxis, holder, selection, pointWidth, times, width, disableSelection]);

    useEffect(() => {
        if (width && countsData) {
            const newPointWidth = getPointWidth(isStatus ? statusDef : statusBreakoutDef, countsData, width);

            if (newPointWidth !== pointWidth && newPointWidth) {
                setPointwidth(newPointWidth);
            }
        }
    }, [width, countsData, pointWidth, isStatus]);

    const callbacks = useMemo(() => {
        return disableSelection
            ? {}
            : {
                  onClick: (ev) => {
                      selectedTimeActions.setValue(SELECTED_TIME, ev.point.x);
                  },
              };
    }, [disableSelection, selectedTimeActions]);

    useEffect(() => {
        const trendData = data?.trends?.[metricName]?.data;
        if (!trendData) return;

        const filteredData = applyStatusFilter(trendData, statusFilter);
        const counts = statusCountsToMetrics(filteredData, times, statusFilter);
        if (!_.isEqual(counts, countsData)) {
            setCountsData(counts);
        }
    }, [data, statusFilter, times, countsData, metricName]);

    if (!countsData) return <Box ref={containerRef} height={height} />;

    const selectedDate = noSelection ? undefined : selection.date;
    const className = noSelection ? classes.statusNoSelection : classes.statusTimeRangePicker;

    return (
        <div className="status-breakout" ref={containerRef}>
            <PerformanceChart
                selectedDate={selectedDate}
                className={className}
                disableLegend
                disableYAxis
                disableTimeAxis={disableTimeAxis}
                initialHeight={height}
                chartDef={isStatus ? statusDef : statusBreakoutDef}
                data={countsData}
                handleEvents={callbacks}
                contained
                xAxis={xAxis}
                yAxisTop={100}
                onSetWidths={setWidths}
                pointWidth={pointWidth}
                hideLine
            />
        </div>
    );
}

/*
    Notes on 2 very similar props:
    "disableSelection" removes the selection marker entirely and prevents it from overwriting the
    time axis with a label showing the selected time (over on the right side), which was needed
    for IPM charts.
    "noSelection" prevents you from making a selection (and changes the cursor), but still shows
    the marker and time range label
*/

StatusBreakout.defaultProps = {
    height: 90,
    noSelection: undefined,
    disableSelection: false,
    onSetPositions: _.noop,
    onTimeRangeChange: _.noop,
};
